/**
 * ZyMedElite MoEngage event taxonomy (P0 launch list).
 *
 * The PWA runs inside the ZyMedElite native shell, which owns the MoEngage SDK and the
 * identified user. Events are therefore relayed to native via the WebView bridge
 * (`{ action: "trackEvent", data: { event, attributes } }`) and native stamps the
 * device / OS / timestamp / app-version fields before tracking. In a plain browser the
 * MoEngage web SDK is used when present so the events are still visible while testing.
 *
 * Only fires for the ZyMedElite app type — MedEco / TatvaCare are untouched.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { isZymedApp } from "./appConfig";
import { getDecodedToken } from "./localStorage";
import { sendMessageToParent } from "./helper";

export const ZYMED_EVENTS = {
  // Home
  HOME_SCREEN_VIEWED: "ZymHome_ScreenViewed",
  HOME_PROFILE_ICON_CLICKED: "ZymHome_ProfileIconClicked",
  HOME_ACCOUNT_SCREEN_VIEWED: "ZymHome_AccountScreenViewed",
  HOME_LOGOUT_CLICKED: "ZymHome_LogoutClicked",

  // ZymedPedia
  PEDIA_OPENED: "ZymPedia_Opened",
  PEDIA_SEARCH_INITIATED: "ZymPedia_SearchInitiated",
  PEDIA_SEARCH_SUGGESTION_CLICKED: "ZymPedia_SearchSuggestionClicked",
  PEDIA_SEARCH_RESULT_FETCHED: "ZymPedia_SearchResultFetched",
  PEDIA_TAB_SWITCHED: "ZymPedia_TabSwitched",
  PEDIA_CATEGORY_FILTER_SELECTED: "ZymPedia_CategoryFilterSelected",
  PEDIA_BOTTOM_NAV_CLICKED: "ZymPedia_BottomNavClicked",
  PEDIA_PERSONALISE_FEED_OPENED: "ZymPedia_PersonaliseFeedOpened",
  PEDIA_SPECIALTY_SELECTED: "ZymPedia_SpecialtySelected",
  PEDIA_PERSONALISE_FEED_SAVED: "ZymPedia_PersonaliseFeedSaved",
  PEDIA_ARTICLE_OPEN: "ZymPedia_ArticleOpen",
  PEDIA_ARTICLE_CLOSED: "ZymPedia_ArticleClosed",
  PEDIA_READ_ARTICLE_CLICKED: "ZymPedia_ReadArticleClicked",
  PEDIA_LIKE_ARTICLE: "ZymPedia_Like_Article",
  PEDIA_SHARE_ARTICLE: "ZymPedia_Share_Article",
  PEDIA_ARTICLE_DOWNLOADED: "ZymPedia_ArticleDownloaded",
  PEDIA_SAVE_TO_LIBRARY: "ZymPedia_SavetoLibrary",
  PEDIA_COMMENT_SUBMITTED: "ZymPedia_CommentSubmitted",
  PEDIA_CONTENT_PLAYBACK_STARTED: "ZymPedia_ContentPlaybackStarted",
  PEDIA_CONTENT_PLAYBACK_COMPLETED: "ZymPedia_ContentPlaybackCompleted",
  PEDIA_PLAYBACK_SPEED_CHANGED: "ZymPedia_PlaybackSpeedChanged",
  PEDIA_BOOKMARKS: "ZymPedia_Bookmarks",
  PEDIA_LIKES: "ZymPedia_Likes",
  PEDIA_QUIZ_TAB_VIEWED: "ZymPedia_QuizTabViewed",
  PEDIA_QUIZ_STARTED: "ZymPedia_QuizStarted",
  PEDIA_QUIZ_QUESTION_ANSWERED: "ZymPedia_QuizQuestionAnswered",
  PEDIA_QUIZ_COMPLETED: "ZymPedia_QuizCompleted",
  PEDIA_QUIZ_RESULT_VIEWED: "ZymPedia_QuizResultViewed",
  PEDIA_FINISHED_QUIZ_RESULT_REOPENED: "ZymPedia_FinishedQuizResultReopened",

  // Generate DDx
  DDX_OPENED: "ZymDdx_Opened",
  DDX_GET_STARTED: "ZymDdx_GetStarted",
  DDX_GENDER_SELECTED: "ZymDdx_GenderSelected",
  DDX_AGE_SELECTED: "ZymDdx_AgeSelected",
  DDX_EDIT_PROMPT: "ZymDdx_EditPrompt",
  DDX_SYMPTOM_INPUT_STARTED: "ZymDdx_SymptomInputStarted",
  DDX_SYMPTOM_VOICE_RECORDING_SUBMITTED: "ZymDdx_SymptomVoiceRecordingSubmitted",
  DDX_SYMPTOM_VOICE_RECORDING_DELETED: "ZymDdx_SymptomVoiceRecordingDeleted",
  DDX_SYMPTOM_PROCESSING: "ZymDdx_SymptomProcessing",
  DDX_SYMPTOM_SUBMITTED: "ZymDdx_SymptomSubmitted",
  DDX_RESPOND: "ZymDdx_Respond",
  DDX_GENERATED: "ZymDdx_Generated",
  DDX_GENERATION_FAILED: "ZymDdx_GenerationFailed",
  DDX_HISTORY_OPENED: "ZymDdx_HistoryOpened",
  DDX_HISTORY_ITEM_CLICKED: "ZymDdx_HistoryItemClicked",
  DDX_EXIT_CONFIRMATION_SHOWN: "ZymDdx_ExitConfirmationShown",
  DDX_NEW_CHAT_CONFIRMED: "ZymDdx_NewChatConfirmed",
  DDX_NEW_CHAT_CANCELLED: "ZymDdx_NewChatCancelled",

  // ZymedAI — these are emitted by the tatva-ai iframe and relayed through
  // TatvaAi.tsx; they are listed here so the catalog lives in one place.
  AI_OPENED: "ZymAI_Opened",
  AI_SUBMIT: "ZymAI_Submit",
  AI_MICROPHONE: "ZymAI_Microphone",
  AI_QUERY_PROCESSING: "ZymAI_QueryProcessing",
  AI_ANSWER_GENERATED: "ZymAI_AnswerGenerated",
  AI_COPY: "ZymAI_Copy",
  AI_LISTEN_CLICKED: "ZymAI_ListenClicked",
  AI_SOURCES_EXPANDED: "ZymAI_SourcesExpanded",
  AI_SOURCE_LINK_CLICKED: "ZymAI_SourceLinkClicked",
  AI_FOLLOW_UP_ASKED: "ZymAI_FollowUpAsked",
  AI_ATTACHMENT_CLICKED: "ZymAI_AttachmentClicked",
  AI_HISTORY: "ZymAI_History",
  AI_THREAD_OPENED: "ZymAI_ThreadOpened",
  AI_THREAD_CLOSED: "ZymAI_ThreadClosed",

  // ZymedShots
  SHOTS_OPENED: "ZymShots_Opened",
  SHOTS_CARD_SWIPED: "ZymShots_CardSwiped",
  SHOTS_BOTTOM_NAV_CLICKED: "ZymShots_BottomNavClicked",
  SHOTS_FEED_TAB_SWITCHED: "ZymShots_FeedTabSwitched",
  SHOTS_FILTER_SORT_CLICKED: "ZymShots_FilterSortClicked",
  SHOTS_ARTICLE_OPENED: "ZymShots_ArticleOpened",
  SHOTS_ARTICLE_CLOSED: "ZymShots_ArticleClosed",
  SHOTS_READ_MORE: "ZymShots_ReadMore",
  SHOTS_LIKE_SHOT: "ZymShots_LikeShot",
  SHOTS_SHARE_SHOT: "ZymShots_shareshot",
  SHOTS_SAVE_SHOT: "ZymShots_saveshot",
  SHOTS_VIEW_MORE: "ZymShots_ViewMore",
  SHOTS_PREFERENCES: "ZymShots_Preferences",
  SHOTS_SAVE_PREFERENCES: "ZymShots_Save_Preferences",
  SHOTS_VISIT_BOOKMARKS: "ZymShots_Visit_BookMarks",

  // Session / auth diagnostics (see zymedSessionDiagnostics.ts)
  SESSION_PEDIA_TOKEN_MINTED: "ZymSession_PediaTokenMinted",
  SESSION_API_AUTH_REJECTED: "ZymSession_ApiAuthRejected",
  SESSION_PEDIA_AUTH_RETRY: "ZymSession_PediaAuthRetry",
  SESSION_FORCED_RELOGIN: "ZymSession_ForcedRelogin",
  SESSION_DEVICE_LIMIT_SHOWN: "ZymSession_DeviceLimitShown",
  SESSION_DEVICE_LOGOUT_CLICKED: "ZymSession_DeviceLogoutClicked",
  SESSION_DEVICE_LOGOUT_RESULT: "ZymSession_DeviceLogoutResult",
  SESSION_ACCOUNT_LOGOUT_RESULT: "ZymSession_AccountLogoutResult",
  SESSION_DEVICE_ID_CREATED: "ZymSession_DeviceIdCreated",
} as const;

export type ZymedEventName = (typeof ZYMED_EVENTS)[keyof typeof ZYMED_EVENTS];

/** `source` values for the `<Module>_Opened` events, keyed by where the tap happened. */
export const ZYMED_SOURCE = {
  TOP_NAV_ICON: "top_nav_icon",
  HOME_BANNER: "home_banner",
  HOME_VIEW_MORE: "home_view_more",
  HOME_CARD_CLICK: "home_card_click",
  HOME_CARD_CTA: "home_card_cta",
  HOME_SCROLL_CARD: "home_scroll_card",
  DIRECT: "direct",
} as const;

/**
 * Entry-point source hand-off. The Home tap handler stores the source right before it
 * navigates; the feature page consumes it on mount. sessionStorage rather than router
 * state because ZymedAI already uses `state` for its iframe URL string.
 */
const TRACK_SOURCE_KEY = "zym_track_source";

const NATIVE_ACTION = "trackEvent";

const getUserAttributes = () => {
  const token: any = getDecodedToken();
  const user = token?.user || {};
  const attrs: Record<string, string> = {};
  if (user.b2cId !== undefined && user.b2cId !== null) attrs.user_id = String(user.b2cId);
  if (user.name) attrs.username = user.name;
  if (user.mobileNumber) attrs.user_mobile_no = String(user.mobileNumber);
  if (user.specialityTxt) attrs.user_specialty = user.specialityTxt;
  return attrs;
};

const stripEmpty = (attrs: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(attrs).filter(([, v]) => v !== undefined && v !== null && v !== "")
  );

export const trackZymedEvent = (
  event: ZymedEventName,
  attributes: Record<string, unknown> = {}
) => {
  if (!isZymedApp()) return;
  const payload = stripEmpty({ ...getUserAttributes(), ...attributes });

  if (window.ReactNativeWebView) {
    sendMessageToParent(NATIVE_ACTION, { event, attributes: payload });
    return;
  }

  // Browser fallback (no native shell): MoEngage web SDK if loaded, else console.
  const moe = (window as any).Moengage;
  if (moe?.track_event) {
    moe.track_event(event, {
      ...payload,
      timestamp: new Date().toISOString(),
      platform: "web",
    });
  } else if (process.env.NODE_ENV !== "production") {
    console.log("[ZymedAnalytics]", event, payload);
  }
};

/**
 * Fires an event at most once per history entry, so remounts caused by returning to a
 * page via back navigation do not re-count as a fresh open.
 */
const firedKeys = new Set<string>();
export const trackZymedEventOncePerLocation = (
  locationKey: string,
  event: ZymedEventName,
  attributes: Record<string, unknown> = {}
) => {
  const dedupeKey = `${event}:${locationKey}`;
  if (firedKeys.has(dedupeKey)) return;
  firedKeys.add(dedupeKey);
  trackZymedEvent(event, attributes);
};

/** Call right before navigating to a feature from Home. Keyed by the `_Opened` event. */
export const setTrackSource = (event: ZymedEventName, source: string) => {
  try {
    sessionStorage.setItem(TRACK_SOURCE_KEY, JSON.stringify({ event, source }));
  } catch {
    // sessionStorage unavailable — the event still fires with `direct`.
  }
};

/** Maps a Home navigation target to the `<Module>_Opened` event it should attribute. */
const openedEventForPath = (path: string): ZymedEventName | null => {
  if (path.startsWith("/tatvapedia") || path === "/content") return ZYMED_EVENTS.PEDIA_OPENED;
  if (path.startsWith("/generate-ddx") || path.startsWith("/ddx-welcome") || path === "/about-ddx")
    return ZYMED_EVENTS.DDX_OPENED;
  if (path.startsWith("/tatva-ai")) return ZYMED_EVENTS.AI_OPENED;
  if (path.startsWith("/tatva-shots") || path === "/short") return ZYMED_EVENTS.SHOTS_OPENED;
  return null;
};

/** One-liner for Home tap handlers: records `source` for whatever feature `path` opens. */
export const markFeatureEntry = (path: string, source: string) => {
  if (path.startsWith("/tatvapedia/quiz")) setQuizEntrySource(source);
  const event = openedEventForPath(path);
  if (event) setTrackSource(event, source);
};

/**
 * Reads and clears the pending source for `event`. Returns null when no Home tap
 * preceded this mount (back navigation, in-feature routing, deep link).
 */
export const consumeTrackSource = (event: ZymedEventName): string | null => {
  try {
    const raw = sessionStorage.getItem(TRACK_SOURCE_KEY);
    if (!raw) return null;
    const pending = JSON.parse(raw) as { event?: string; source?: string };
    if (pending?.event !== event) return null;
    sessionStorage.removeItem(TRACK_SOURCE_KEY);
    return pending.source || null;
  } catch {
    return null;
  }
};

/**
 * Fires `<Module>_Opened` once per history entry when the feature page mounts.
 * With `requireSource`, it only fires when a Home tap stored a source first — used on
 * routes that are also reached from inside the feature itself. `skip` still consumes any
 * pending source (so it cannot leak to a later open) but fires nothing.
 */
export const useZymedOpenedEvent = (
  event: ZymedEventName,
  options: { requireSource?: boolean; skip?: boolean } = {}
) => {
  const location = useLocation();
  useEffect(() => {
    if (!isZymedApp()) return;
    const source = consumeTrackSource(event);
    if (options.skip) return;
    if (options.requireSource && !source) return;
    trackZymedEventOncePerLocation(location.key, event, {
      source: source || ZYMED_SOURCE.DIRECT,
    });
    // Mount-only by design: the location key identifies this visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const HOME_SEEN_SESSION_KEY = "zym_home_seen";
const RETURNING_USER_KEY = "zym_returning_user";

/**
 * ZymHome_ScreenViewed. entry_point is inferred: the first Home render in this page
 * session is the post-login landing (`login_success`); any later one is `nav_back`.
 * `app_reopen` cannot be told apart from `nav_back` inside the WebView — the native
 * shell keeps the page alive across backgrounding, so there is no fresh render.
 */
export const useZymedHomeViewed = () => {
  const location = useLocation();
  useEffect(() => {
    if (!isZymedApp()) return;
    let firstThisSession = true;
    let isReturningUser = false;
    try {
      firstThisSession = !sessionStorage.getItem(HOME_SEEN_SESSION_KEY);
      isReturningUser = !!localStorage.getItem(RETURNING_USER_KEY);
      sessionStorage.setItem(HOME_SEEN_SESSION_KEY, "1");
      localStorage.setItem(RETURNING_USER_KEY, "1");
    } catch {
      // storage unavailable — fall through with defaults
    }
    trackZymedEventOncePerLocation(location.key, ZYMED_EVENTS.HOME_SCREEN_VIEWED, {
      entry_point: firstThisSession ? "login_success" : "nav_back",
      is_returning_user: isReturningUser,
    });

    // The native shell dispatches this when the app returns from the background; Home
    // does not remount then, so it is the only way to count an `app_reopen` view.
    const onAppReopen = () =>
      trackZymedEvent(ZYMED_EVENTS.HOME_SCREEN_VIEWED, {
        entry_point: "app_reopen",
        is_returning_user: true,
      });
    window.addEventListener("zymAppReopen", onAppReopen);
    return () => window.removeEventListener("zymAppReopen", onAppReopen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

/**
 * The content fields the taxonomy asks for on every ZymedPedia article event, pulled
 * off whichever shape the API returned (detail responses, feed cards and search hits
 * each name the specialty differently).
 */
export const pediaContentAttrs = (content: any) => ({
  content_id: content?.id ?? content?.docId,
  content_name: content?.contentTitle ?? content?.title,
  content_type: content?.contentType,
  therapeutic_area:
    content?.speciality ??
    content?.specialityName ??
    content?.therapeuticArea ??
    content?.consolidatedModule,
});

/**
 * Measures how long a detail view stayed open, for the `*_ArticleClosed` events.
 * Returns a `stop` that reports the elapsed time once — calling it again (unmount after
 * an explicit back tap) yields undefined rather than a duplicate event.
 */
export const startDwellTimer = () => {
  let startedAt: number | null = Date.now();
  return {
    stopMinutes: (): number | undefined => {
      if (startedAt === null) return undefined;
      const minutes = Math.round(((Date.now() - startedAt) / 60000) * 100) / 100;
      startedAt = null;
      return minutes;
    },
    stopSeconds: (): number | undefined => {
      if (startedAt === null) return undefined;
      const seconds = Math.round((Date.now() - startedAt) / 100) / 10;
      startedAt = null;
      return seconds;
    },
  };
};

/**
 * How long the article currently on screen has been open. The like/share buttons live in
 * a leaf component that does not own the detail view's lifecycle, so the open time is
 * kept here rather than threaded through props.
 */
let pediaArticleOpenedAt: number | null = null;
export const markPediaArticleOpen = () => {
  pediaArticleOpenedAt = Date.now();
};
export const pediaArticleMinutesSoFar = (): number | undefined =>
  pediaArticleOpenedAt === null
    ? undefined
    : Math.round(((Date.now() - pediaArticleOpenedAt) / 60000) * 100) / 100;

/**
 * `search_origin` hand-off for ZymPedia_SearchInitiated. The Home widget is only a
 * shortcut into the search tab — the query itself is always typed there — so the Home
 * tap records where the visit came from and the first search of that visit claims it.
 */
const SEARCH_ORIGIN_KEY = "zym_search_origin";
export const setPediaSearchOrigin = (origin: string) => {
  try {
    sessionStorage.setItem(SEARCH_ORIGIN_KEY, origin);
  } catch {
    // storage unavailable — the search is reported as in_app_search_tab
  }
};
export const consumePediaSearchOrigin = (): string => {
  try {
    const origin = sessionStorage.getItem(SEARCH_ORIGIN_KEY);
    if (origin) {
      sessionStorage.removeItem(SEARCH_ORIGIN_KEY);
      return origin;
    }
  } catch {
    // ignore
  }
  return "in_app_search_tab";
};

/**
 * `source` hand-off for ZymPedia_QuizTabViewed, which can be reached from the Home
 * banner as well as from the ZymedPedia bottom bar. Same pattern as the search origin
 * above: the Home tap records it, the quiz list claims it on mount.
 */
const QUIZ_SOURCE_KEY = "zym_quiz_source";
export const setQuizEntrySource = (source: string) => {
  try {
    sessionStorage.setItem(QUIZ_SOURCE_KEY, source);
  } catch {
    // storage unavailable — the view is reported as pedia_bottom_nav
  }
};
export const consumeQuizEntrySource = (): string => {
  try {
    const source = sessionStorage.getItem(QUIZ_SOURCE_KEY);
    if (source) {
      sessionStorage.removeItem(QUIZ_SOURCE_KEY);
      return source;
    }
  } catch {
    // ignore
  }
  return "pedia_bottom_nav";
};

/**
 * A thread is identified by its opening question, which is free clinical text. Only the
 * leading fragment is sent — enough to recognise the thread in a report, bounded so a
 * long consultation question is not shipped wholesale to the analytics provider. Mirrors
 * zymedThreadTopic in the tatva-ai app, which reports threads opened from its own tab.
 */
export const zymedThreadTopic = (text?: string | null) =>
  (text || "").trim().slice(0, 120) || undefined;
